import { memo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
	Box,
	Typography,
	Button,
	Avatar,
	Grid2 as Grid,
	Stack,
} from '@mui/material';
import {
	fetchCoursesByCreator,
	// deleteCourse,
	// fetchCourseResultsByStudent,
	// getCourseInfo,
	// getSortedCoursesList,
	// fetchCourseProgress,
	// updateCurriculum
} from '../../utils/api';
import { USER_TYPE } from '../../config/const';
import SoopraIcon from '../../assets/images/soopra-icon.png';
// import { TierContext } from "../../context/TierContext";
import DefaultUserImage from '../../assets/images/default-avatar.png';
import { UserFormType } from '../creatorOnBoarding/signup.types';

interface UserCourseDataProps {
	authUser?: UserFormType;
	profileData?: UserFormType;
}

// const showMyCourse = process.env.REACT_APP_SHOW_MY_COURSE === 'true';
// const allowSelfPublishedCourses = process.env.REACT_APP_ALLOW_SELF_PUBLISHED_COURSES === 'true';

export const setLoadingState = (
	setFunc: React.Dispatch<
		React.SetStateAction<{ data: any; isLoading: boolean }>
	>,
	state: boolean
) => {
	setFunc((info) => {
		let copy = { ...info };
		copy.isLoading = state;
		return copy;
	});
};

const UserCourseData: React.FC<UserCourseDataProps> = (props) => {
	const { authUser, profileData } = props;
	const [courses, setCourses] = useState({ data: [], isLoading: false });

	const {
		userId = '',
		userName = '',
		index = '',
	} = profileData || {};
	const { documentId: authDocumentId = '' } = authUser || {};

	const navigate = useNavigate();

	useEffect(() => {
		if (
			authUser?.userType === USER_TYPE.CREATOR &&
			userId === authDocumentId
		) {
			loadCourseData();
		}
	}, [userId]); //eslint-disable-line react-hooks/exhaustive-deps

	const loadCourseData = async () => {
		setLoadingState(setCourses, true);
		fetchCoursesByCreator(userId)
			.then((data) => {
				data.forEach((course) => {
					course.creator_avatar ||= authUser?.profilePicUrl;
					course.creator_name ||= authUser?.fullName;
				});
				setCourses((info) => {
					let copy = { ...info };
					copy.data = data;
					return copy;
				});
			})
			.catch((error) => {
				console.error('Error fetching courses:', error);
			})
			.finally(() => {
				setLoadingState(setCourses, false);
			});
	};

	const CourseCard = memo((({ course }: { course: any; }) => (
		<Grid
			size={{
				xs: 12,
				sm: 6,
				md: 3,
			}}
			sx={{ display: 'flex', justifyContent: 'center' }}
		>
			<Box
				component={'a'}
				href={`/courses/${course?.course_id}`}
				sx={{
					display: 'flex',
					flexDirection: 'column',
					borderRadius: '8px',
					boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
					overflow: 'hidden',
					backgroundColor: (theme) =>
						theme.palette.background['paper-elevation-2'],
					width: '100%',
					textDecoration: 'none',
				}}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					navigate(`/courses/${course?.course_id}`);
				}}
			>
				<Avatar
					src={
						course?.banner_image ||
						course?.creator_avatar ||
						SoopraIcon
					}
					alt={course?.name || 'course-banner'}
					sx={{
						width: '100%',
						height: 'auto',
						objectFit: 'cover',
						aspectRatio: '1.78',
						borderRadius: (theme) => theme.shape.borderRadius,
					}}
				/>
				<Box p={1.5}>
					<Typography
						component='p'
						variant='body2'
						color='text.secondary'
						align='left'
					>
						{course?.subject}
					</Typography>
					<Typography
						variant='h7'
						color='text.primary'
						align='left'
						pt={0.5}
					>
						{course?.name}
					</Typography>
					<Stack direction={'row'} alignItems='center' pt={0.5}>
						<Avatar
							sx={{ width: 24, height: 24 }}
							alt={course?.creator_name}
							src={course?.creator_avatar || DefaultUserImage}
						/>
						<Typography
							component='p'
							variant='body3'
							color='text.secondary'
							align='left'
							pl={1}
						>
							{course?.creator_name}
						</Typography>
					</Stack>
				</Box>
			</Box>
		</Grid>
	)));

	const CreatedCourses = () => {
		return courses.data.length || courses.isLoading ? (
			<Box
				mt={3}
				display='flex'
				flexDirection='column'
				width='100%'
				gap={1.5}
			>
				<Box
					display='flex'
					alignItems='center'
					justifyContent='space-between'
				>
					<Typography color={'text.secondary'} variant='body2'>
						{`${
							authUser?.userName === profileData.userName
								? 'My '
								: ''
						}Courses`}
					</Typography>
					<Box display='flex' alignItems='center'>
						<Link
							id={index ? `href-create-course-${userName}` : ''}
							to={index ? `/createCourse` : ''}
							aria-disabled={!index}
							style={{
								cursor: index ? 'pointer' : 'not-allowed',
								textDecoration: 'none',
							}}
						>
							<Button
								id='button-create-course'
								variant='contained'
								size='medium'
								color='primary'
								sx={{
									py: 1,
								}}
							>
								Create New Course
							</Button>
						</Link>
					</Box>
				</Box>
				{courses.isLoading ? (
					<Typography>Loading...</Typography>
				) : courses.data.length > 0 ? (
					<Grid container spacing={3}>
						{courses.data.map((course) => (
							<CourseCard
								key={course.course_id}
								course={course}
							/>
						))}
					</Grid>
				) : (
					<Typography>No created courses found.</Typography>
				)}
			</Box>
		) : (
			<></>
		);
	};

	return (
		<>
			<CreatedCourses />
		</>
	);
};

export default UserCourseData;
